/**
 * CycleStrategicView
 *
 * Read-only strategic overview of a Cycle for Sponsors and Observers.
 * Shows: cycle health, lane status, ball positions, session completion,
 * and plan-of-record items when the cycle closes.
 *
 * No edit controls, no configuration, no admin tabs.
 * The goal: a sponsor or board member can open this and immediately understand
 * where the cycle stands and whether it's on track.
 *
 * Canon: §1D Goodness Principle
 */

import { useMemo } from 'react';
import {
  Activity,
  CheckCircle2,
  Circle,
  Clock,
  GitBranch,
  Layers,
} from 'lucide-react';
import type { DegapCycle } from '../../types/prioritization/degapCycle';
import type { CatchballLoop } from '../../types/prioritization/catchballLoop';
import type { PrioritizationSession } from '../../types/prioritization/session';
import type { Track } from '../../types/prioritization/track';

// ── Status helpers ─────────────────────────────────────────────────────────────

function cycleHealthColor(cycle: DegapCycle): string {
  if (cycle.status === 'active') return 'text-[var(--green)] dark:text-[var(--text-ter)]';
  if (cycle.status === 'draft') return 'text-[var(--mustard)]';
  if (cycle.status === 'complete' || cycle.status === 'archived') return 'text-[var(--text-ter)]';
  return 'text-[var(--text-ter)]';
}

function cycleHealthLabel(cycle: DegapCycle): string {
  switch (cycle.status) {
    case 'draft':    return 'Setting up';
    case 'active':   return 'In progress';
    case 'complete': return 'Complete';
    case 'archived': return 'Archived';
    default:         return cycle.status ?? 'Unknown';
  }
}

function loopStateLabel(state: string | null | undefined): string {
  switch (state?.toUpperCase()) {
    case 'OPEN':               return 'Active';
    case 'MIDDLE_WORKING':     return 'Working';
    case 'BOTTOM_WORKING':     return 'Working';
    case 'PROPOSED':           return 'Proposal submitted';
    case 'REVISION_REQUESTED': return 'Revision requested';
    case 'ACCEPTED':           return 'Accepted';
    case 'FINAL_REJECTED':     return 'Rejected';
    case 'DECLINED':           return 'Closed';
    default:                   return state ?? 'Unknown';
  }
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function StatCard({
  label,
  value,
  sub,
}: {
  label: string;
  value: string | number;
  sub?: string;
}) {
  return (
    <div className="border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] px-5 py-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)] dark:text-[var(--text-ter)] mb-1">
        {label}
      </p>
      <p className="text-2xl font-bold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
        {value}
      </p>
      {sub && (
        <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-sec)] mt-0.5">{sub}</p>
      )}
    </div>
  );
}

function LoopCard({
  loop,
}: {
  loop: CatchballLoop;
}) {
  const stateLabel = loopStateLabel(loop.state);
  const isActive = !['ACCEPTED', 'FINAL_REJECTED', 'DECLINED'].includes(
    loop.state?.toUpperCase() ?? '',
  );

  return (
    <div className="flex items-center justify-between gap-3 py-3 border-b border-[var(--rule)] dark:border-[var(--rule)] last:border-0">
      <div className="flex items-center gap-3 min-w-0">
        <div
          className={`w-2 h-2 rounded-full flex-shrink-0 ${
            isActive ? 'bg-[var(--mustard)] animate-pulse' : 'bg-[var(--rule-bright)]'
          }`}
        />
        <div className="min-w-0">
          <p className="text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)]">
            Loop {loop.loopNumber}
          </p>
          <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">
            {stateLabel}
          </p>
        </div>
      </div>
      <span
        className={`flex-shrink-0 text-xs font-medium px-2 py-0.5 rounded-full ${
          isActive
            ? 'bg-[var(--mustard-dim)] text-[var(--mustard)] dark:bg-[var(--mustard)]/20 dark:text-[var(--mustard)]'
            : 'bg-[var(--hover-tint)] text-[var(--text-ter)] dark:text-[var(--text-ter)]'
        }`}
      >
        {stateLabel}
      </span>
    </div>
  );
}

function TrackProgressCard({
  track,
  sessions,
}: {
  track: Track;
  sessions: PrioritizationSession[];
}) {
  const totalSessions = sessions.length;
  const completedSessions = sessions.filter(
    (s) => s.state === 'RUN_COMPLETE' || s.state === 'PUBLISHED',
  ).length;
  const pct = totalSessions > 0 ? Math.round((completedSessions / totalSessions) * 100) : 0;
  const hasResult = !!track.officialSessionId;

  return (
    <div className="flex items-center gap-4 py-3 border-b border-[var(--rule)] dark:border-[var(--rule)] last:border-0">
      <Layers className="h-4 w-4 text-[var(--text-ter)] flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)] truncate">
          {track.name}
        </p>
        <div className="flex items-center gap-2 mt-1">
          <div className="flex-1 h-1.5 bg-[var(--hover-tint)] rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ${
                hasResult ? 'bg-[var(--green)]' : 'bg-[var(--slate)]'
              }`}
              style={{ width: `${pct}%` }}
            />
          </div>
          <span className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] flex-shrink-0">
            {completedSessions}/{totalSessions} sessions
          </span>
        </div>
      </div>
      {hasResult ? (
        <CheckCircle2 className="h-4 w-4 text-[var(--green)] flex-shrink-0" />
      ) : (
        <Circle className="h-4 w-4 text-[var(--text-ter)] flex-shrink-0" />
      )}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

interface CycleStrategicViewProps {
  cycle: DegapCycle;
  loops: CatchballLoop[];
  tracks: Track[];
  sessions: PrioritizationSession[];
}

export function CycleStrategicView({
  cycle,
  loops,
  tracks,
  sessions,
}: CycleStrategicViewProps) {
  const activeLoops = useMemo(
    () =>
      loops.filter(
        (l) =>
          !['ACCEPTED', 'FINAL_REJECTED', 'DECLINED'].includes(
            l.state?.toUpperCase() ?? '',
          ),
      ),
    [loops],
  );

  const completedSessions = useMemo(
    () => sessions.filter((s) => s.state === 'RUN_COMPLETE' || s.state === 'PUBLISHED').length,
    [sessions],
  );

  const tracksWithResult = useMemo(
    () => tracks.filter((t) => !!t.officialSessionId).length,
    [tracks],
  );

  const dueDate = cycle.completionTargetDate ?? cycle.targetDate;
  const daysRemaining = useMemo(() => {
    if (!dueDate) return null;
    return Math.ceil((new Date(dueDate).getTime() - Date.now()) / 86400000);
  }, [dueDate]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2 mb-1">
          <Activity className={`h-4 w-4 ${cycleHealthColor(cycle)}`} />
          <p className={`text-sm font-semibold ${cycleHealthColor(cycle)}`}>
            {cycleHealthLabel(cycle)}
          </p>
        </div>
        <h2 className="text-lg font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
          {cycle.name}
        </h2>
        {cycle.description && (
          <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)] mt-1">
            {cycle.description}
          </p>
        )}
        {daysRemaining !== null && (
          <p
            className={`text-xs mt-2 flex items-center gap-1 ${
              daysRemaining < 7 ? 'text-[var(--coral)]' : 'text-[var(--text-ter)] dark:text-[var(--text-ter)]'
            }`}
          >
            <Clock className="h-3.5 w-3.5" />
            {daysRemaining > 0
              ? `${daysRemaining} days remaining`
              : `${Math.abs(daysRemaining)} days overdue`}
          </p>
        )}
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Sessions" value={completedSessions} sub={`of ${sessions.length} complete`} />
        <StatCard label="Tracks" value={tracksWithResult} sub={`of ${tracks.length} have results`} />
        <StatCard label="Active loops" value={activeLoops.length} />
        <StatCard label="Status" value={cycleHealthLabel(cycle)} />
      </div>

      {/* Catchball lane status */}
      {loops.length > 0 && (
        <div className="border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)]">
          <div className="flex items-center gap-2 px-6 pt-5 pb-3 border-b border-[var(--rule)] dark:border-[var(--rule)]">
            <GitBranch className="h-4 w-4 text-[var(--text-ter)]" />
            <h3 className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
              Catchball lanes
            </h3>
          </div>
          <div className="px-6 py-1">
            {loops.map((l) => (
              <LoopCard key={l.id} loop={l} />
            ))}
          </div>
        </div>
      )}

      {/* Track progress */}
      {tracks.length > 0 && (
        <div className="border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)]">
          <div className="flex items-center gap-2 px-6 pt-5 pb-3 border-b border-[var(--rule)] dark:border-[var(--rule)]">
            <Layers className="h-4 w-4 text-[var(--text-ter)]" />
            <h3 className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
              Tracks
            </h3>
          </div>
          <div className="px-6 py-1">
            {tracks.map((t) => (
              <TrackProgressCard
                key={t.id}
                track={t}
                sessions={sessions.filter((s) => s.trackId === t.id)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Empty state */}
      {loops.length === 0 && tracks.length === 0 && (
        <div className="border border-dashed border-[var(--rule)] dark:border-[var(--rule)] px-6 py-10 text-center">
          <Activity className="h-6 w-6 text-[var(--text-ter)] mx-auto mb-3" />
          <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)]">
            This cycle is still being set up.
          </p>
        </div>
      )}
    </div>
  );
}

export default CycleStrategicView;
